<script setup>
import { InitViewIDs } from '@src/config';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import {
  FunctionAnalysisType,
  getDefaultFunctionAnalysisState,
  useFunctionCalculatorStore,
} from '../store/calculators/FunctionCalculatorStore';
import { ContourLabel, encodeContourLabelItemId } from '../store/contours/contour-definitions';
import { useContourEventStore } from '../store/contours/contour-event-store';
import { useViewportStateStore } from '../store/contours/viewport-state-store';
import { useCurrentStudyStore } from '../store/dicom-web/current-study-store';
import FunctionResultTable from './FunctionResultTable.vue';
import Polarmap from './Polarmap.vue';
import SeriesTable from './SeriesTable.vue';
import ToolButton from './ToolButton.vue';
import VtkTwoView from './VtkTwoView.vue';

const functionStore = useFunctionCalculatorStore();
const viewportStateStore = useViewportStateStore();

const currentStudyStore = useCurrentStudyStore();

const handleContourChange = () => {
  functionStore.calculate();
};

// contour events
const unsub = useContourEventStore().on(() => {
  handleContourChange();
});
onBeforeUnmount(unsub);

const currentImageIndices = computed(() =>
  viewportStateStore.getSelectedImageIndices(InitViewIDs.FunctionMainView),
);
const currentSeriesIndex = computed(() => currentImageIndices.value.seriesIdx);
const currentImageIndicesSecondary = computed(() =>
  viewportStateStore.getSelectedImageIndices(InitViewIDs.FunctionSecondaryView),
);
const currentSeriesIndexSecondary = computed(
  () => currentImageIndicesSecondary.value.seriesIdx,
);
const currentOrientationSax = computed(() => functionStore.getIsSaxAnalysis());

const isAuto = ref(true);
const showShortening = ref(false);
const selectedOrientation = ref('sax');

const primaryViewTitle = computed(() =>
  currentOrientationSax.value ? 'Short Axis Stack' : 'Long Axis 2CV',
);
const secondaryViewTitle = computed(() =>
  currentOrientationSax.value ? 'Reference' : 'Long Axis 4CV',
);
const analysisOneTitle = computed(() =>
  currentOrientationSax.value ? 'Sax Stack' : 'Lax Monoplane 2CV',
);
const analysisTwoTitle = computed(() =>
  currentOrientationSax.value ? '' : 'Lax Monoplane 4CV',
);
const analysisThreeTitle = computed(() =>
  currentOrientationSax.value ? '' : 'Lax Biplane',
);

// update calc
const functionMyo = computed(() => {
  const functionAnalysisStates = [];
  if (currentOrientationSax.value) {
    const functionAnalysisState
      = functionStore.$state.functionAnalysisStates[
        FunctionAnalysisType.SaxStack
      ];
    functionAnalysisStates.push(
      functionAnalysisState || getDefaultFunctionAnalysisState(),
    );
  }
  else {
    const functionAnalysisState2CV
      = functionStore.$state.functionAnalysisStates[
        FunctionAnalysisType.LaxMonoplane2CV
      ];
    const functionAnalysisState4CV
      = functionStore.$state.functionAnalysisStates[
        FunctionAnalysisType.LaxMonoplane4CV
      ];
    const functionAnalysisStateBiplane
      = functionStore.$state.functionAnalysisStates[
        FunctionAnalysisType.LaxBiplane
      ];

    // console.log('functionAnalysisState2CV', functionAnalysisState2CV);
    // console.log('functionAnalysisState4CV', functionAnalysisState4CV);
    // console.log('functionAnalysisStateBiplane', functionAnalysisStateBiplane);

    functionAnalysisStates.push(
      functionAnalysisState2CV || getDefaultFunctionAnalysisState(),
    );
    functionAnalysisStates.push(
      functionAnalysisState4CV || getDefaultFunctionAnalysisState(),
    );
    functionAnalysisStates.push(
      functionAnalysisStateBiplane || getDefaultFunctionAnalysisState(),
    );
  }
  return functionAnalysisStates;
});

const wallThickening = computed(() => {
  // return [];
  if (currentOrientationSax.value) {
    return functionMyo.value[0]?.wallThickening ?? [];
  }
  return [];
});

const systoleLVIndices = computed(() => {
  console.log(
    'systoleLVIndices',
    functionMyo.value.map(state => state?.indexLVSystole ?? -1),
  );
  return functionMyo.value.map(state => state?.systolicPhase ?? -1);
});

const diastoleLVIndices = computed(() => {
  console.log(
    'diastoleLVIndices',
    functionMyo.value.map(state => state?.indexLVDiastole ?? -1),
  );
  return functionMyo.value.map(state => state?.diastolicPhase ?? -1);
});

const systoleRVIndices = computed(() => {
  console.log(
    'systoleRVIndices',
    functionMyo.value.map(state => state?.indexRVSystole ?? -1),
  );
  return functionMyo.value.map(state => state?.systolicPhaseRV ?? -1);
});

const diastoleRVIndices = computed(() => {
  console.log(
    'diastoleRVIndices',
    functionMyo.value.map(state => state?.indexRVDiastole ?? -1),
  );
  return functionMyo.value.map(state => state?.diastolicPhaseRV ?? -1);
});

const updateCrossRef = () => {
  viewportStateStore.clearReferenceImageUids(InitViewIDs.FunctionMainView);
  viewportStateStore.clearReferenceImageUids(InitViewIDs.FunctionSecondaryView);

  if (currentOrientationSax.value) {
    const sliceCount = currentStudyStore.getSliceCount(
      currentImageIndices.value.seriesIdx,
    );

    for (let i = 0; i < sliceCount; i++) {
      const imageId = currentStudyStore.getSliceImageUIDs(
        currentImageIndices.value.seriesIdx,
        i,
      )[0];
      viewportStateStore.addReferenceImage(
        'FunctionSecondaryView',
        imageId,
        false,
      );
      if (i === currentImageIndices.value.sliceIdx) {
        viewportStateStore.setActiveReferenceImageUid(
          'FunctionSecondaryView',
          imageId,
        );
      }
    }
  }
};

watch(
  currentSeriesIndex,
  () => {
    functionStore.updateMain();
    updateCrossRef();
  },
  { immediate: true },
);

watch(
  currentSeriesIndexSecondary,
  () => {
    if (currentOrientationSax.value) {
      updateCrossRef();
    }
    else {
      functionStore.updateMain();
      updateCrossRef();
    }
  },
  { immediate: true },
);

watch(
  currentImageIndices,
  () => {
    // ToDo checks for performance
    updateCrossRef();
  },
  { immediate: true },
);

watch(currentOrientationSax, () => {
  updateContourTools();
  updateVisibleContours(showShortening.value);
});
// watch(currentSeriesIndexSecondary, () => {
// //
// },
//     { immediate: true }
// );

const handleAutoOrientationChange = () => {
  if (isAuto.value) {
    functionStore.$state.autoOrientation = true;
  }
  else {
    functionStore.$state.autoOrientation = false;
    functionStore.$state.isSaxUserSelected
      = selectedOrientation.value === 'sax';
  }
  functionStore.updateMain();
  updateCrossRef();
};

const handleOrientationChange = () => {
  handleAutoOrientationChange();
};

function toggleShortening() {
  showShortening.value = !showShortening.value;
  onToggleShortening();
}
function toggleAutoOrientation() {
  isAuto.value = !isAuto.value;
  handleAutoOrientationChange();
}

const visibleBase = [
  ContourLabel.LaxLvExtendEndo,
  ContourLabel.LaxLvExtendEpi,
  // todo: remove just for debugging
  ContourLabel.LaxLvMyoFusion,
  ContourLabel.SaxLvMyoFusion,
];
const transmuralitySaxBoundaryIds = [
  { label: ContourLabel.SaxLvTransmurality, id: encodeContourLabelItemId([0]) },
  { label: ContourLabel.SaxLvTransmurality, id: encodeContourLabelItemId([100]) },
];
const transmuralityLaxBoundaryIds = [
  { label: ContourLabel.LaxLvTransmurality, id: encodeContourLabelItemId([0]) },
  { label: ContourLabel.LaxLvTransmurality, id: encodeContourLabelItemId([100]) },
];
function updateVisibleContours(showShort) {
  const secondary = [...visibleBase];
  const main = [...visibleBase];
  if (showShort) {
    secondary.push(ContourLabel.LaxLvCenterChord);
    main.push(ContourLabel.LaxLvCenterChord, ContourLabel.SaxLvCenterChord);
  }
  viewportStateStore.setVisibleGeneratedContours(InitViewIDs.FunctionSecondaryView, secondary);
  viewportStateStore.setVisibleGeneratedContours(InitViewIDs.FunctionMainView, main);

  if (showShort) {
    viewportStateStore.setHiddenUserEditableContours(InitViewIDs.FunctionSecondaryView, [ContourLabel.LaxLvEndo, ContourLabel.LaxLvEpi]);
    viewportStateStore.setHiddenUserEditableContours(InitViewIDs.FunctionMainView, [ContourLabel.SaxLvEndo, ContourLabel.SaxLvEpi, ContourLabel.LaxLvEndo, ContourLabel.LaxLvEpi]);
    viewportStateStore.setVisibleGeneratedContourIds(InitViewIDs.FunctionMainView, [...transmuralitySaxBoundaryIds, ...transmuralityLaxBoundaryIds]);
    viewportStateStore.setVisibleGeneratedContourIds(InitViewIDs.FunctionSecondaryView, [...transmuralityLaxBoundaryIds]);
  }
  else {
    viewportStateStore.setHiddenUserEditableContours(InitViewIDs.FunctionSecondaryView, []);
    viewportStateStore.setHiddenUserEditableContours(InitViewIDs.FunctionMainView, []);
    viewportStateStore.setVisibleGeneratedContourIds(InitViewIDs.FunctionMainView, []);
    viewportStateStore.setVisibleGeneratedContourIds(InitViewIDs.FunctionSecondaryView, []);
  }
}
function onToggleShortening() {
  updateVisibleContours(showShortening.value);
  functionStore.setShowShortening(showShortening.value);
  functionStore.calculate();
}

function updateContourTools() {
  const commonContourLabels = [
    ContourLabel.GeneralPolygon,
    ContourLabel.GeneralLine,
    ContourLabel.AnnotationArrow,
    ContourLabel.AnnotationText,
  ];
  const saxContourLabels = [
    ContourLabel.SaxLvEndo,
    ContourLabel.SaxLvEpi,
    ContourLabel.SaxLvOpenEpi,
    ContourLabel.RvInsertion,
    ContourLabel.SaxRvEndo,
    ContourLabel.SaxRvPapillary,
    ContourLabel.SaxLvPapillary,
    ContourLabel.SaxLvExclusion,
  ];
  const laxContourLabels = [
    ContourLabel.LaxLvEndo,
    ContourLabel.LaxLvEpi,
  ];
  const saxSecondaryContourLabels = [
    ContourLabel.LaxLvRange,
  ];

  if (currentOrientationSax.value) {
    viewportStateStore.setAllowedContourLabels(
      InitViewIDs.FunctionMainView,
      [...saxContourLabels, ...commonContourLabels],
    );
    viewportStateStore.setAllowedContourLabels(
      InitViewIDs.FunctionSecondaryView,
      [...commonContourLabels, ...saxSecondaryContourLabels],
    );
  }
  else {
    viewportStateStore.setAllowedContourLabels(
      InitViewIDs.FunctionMainView,
      [...commonContourLabels, ...laxContourLabels],
    );
    viewportStateStore.setAllowedContourLabels(
      InitViewIDs.FunctionSecondaryView,
      [...commonContourLabels, ...laxContourLabels],
    );
  }
}

onMounted(() => {
  updateVisibleContours(showShortening.value);
  updateContourTools();
  functionStore.updateMain();
  updateCrossRef();
});
</script>

<template>
  <div class="vtk-two-view-container">
    <div class="flex flex-1 gap-2">
      <div
        class="viewport-column"
        :class="{ 'equal-views': !currentOrientationSax }"
      >
        <div class="vtk-container">
          <VtkTwoView
            :id="InitViewIDs.FunctionMainView"
            class="vtk-view"
            :title="primaryViewTitle"
          />
        </div>
        <div class="vtk-container">
          <VtkTwoView
            :id="InitViewIDs.FunctionSecondaryView"
            class="vtk-view"
            :title="secondaryViewTitle"
          />
        </div>
      </div>
      <div class="right-column flex flex-column gap-2">
        <div class="report-section simple-panel">
          <div class="flex justify-between w-full mb-4" style="bor">
            <h2>LV Function</h2>
            <div class="orientation-selector glass-panel">
              <div class="checkbox-row">
                <ToolButton
                  :size="28"
                  icon="mdi mdi-heart-pulse"
                  label="Show Thickening/Shortening"
                  tooltip-location="bottom"
                  :class="{ 'tool-active': showShortening }"
                  @click="toggleShortening"
                />
                <ToolButton
                  :size="28"
                  icon="mdi mdi-crosshairs-gps"
                  label="Auto Orientation"
                  tooltip-location="bottom"
                  :class="{ 'tool-active': isAuto }"
                  @click="toggleAutoOrientation"
                />
              </div>
              <select
                v-if="!isAuto"
                v-model="selectedOrientation"
                :disabled="isAuto"
                @change="handleOrientationChange"
              >
                <option value="sax">
                  Short Axis
                </option>
                <option value="lax">
                  Long Axis
                </option>
              </select>
            </div>
          </div>

          <div class="function-tables-container">
            <div class="tables-section">
              <FunctionResultTable
                v-if="functionMyo[0]"
                :results="functionMyo[0]"
                :title="analysisOneTitle"
                :is-sax="currentOrientationSax"
              />
              <FunctionResultTable
                v-if="functionMyo[1]"
                :results="functionMyo[1]"
                :title="analysisTwoTitle"
                :is-sax="currentOrientationSax"
              />
              <FunctionResultTable
                v-if="functionMyo[2]"
                :results="functionMyo[2]"
                :title="analysisThreeTitle"
                :is-sax="currentOrientationSax"
              />
            </div>
            <Polarmap
              v-if="currentOrientationSax"
              :rings-data="wallThickening"
              :highlight-slice="currentImageIndices.sliceIdx"
              title="Wall Thickening"
              unit="%"
            />
          </div>
          <slot name="report" />
        </div>
        <div class="series-table-section flex flex-column gap-2">
          <div class="series-table-wrapper">
            <SeriesTable
              :viewport-id="InitViewIDs.FunctionMainView"
              :index-l-v-systole="systoleLVIndices[0]"
              :index-l-v-diastole="diastoleLVIndices[0]"
              :index-r-v-systole="systoleRVIndices[0]"
              :index-r-v-diastole="diastoleRVIndices[0]"
              :is-sax="currentOrientationSax"
            />
          </div>
          <div v-if="!currentOrientationSax" class="series-table-wrapper">
            <SeriesTable
              :viewport-id="InitViewIDs.FunctionSecondaryView"
              :index-l-v-systole="systoleLVIndices[1]"
              :index-l-v-diastole="diastoleLVIndices[1]"
              :index-r-v-systole="systoleRVIndices[1]"
              :index-r-v-diastole="diastoleRVIndices[1]"
              :is-sax="false"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.vtk-two-view-container {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
}

/* new */
.vtk-two-view-container > .flex {
  min-height: 0;
}

.viewport-column {
  display: flex;
  flex-direction: column;
  width: 55%;
  height: 100%;
  gap: 10px;
}

.right-column {
  width: 45%;
  height: 100%;
  min-height: 0;
}

.vtk-container {
  position: relative;
  display: flex;
  flex-direction: column;
}

.vtk-view {
  width: 100%;
  border-radius: 0;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.vtk-container:first-child {
  flex: 2;
}

.vtk-container:last-child {
  flex: 1;
}

.equal-views .vtk-container:first-child,
.equal-views .vtk-container:last-child {
  flex: 1;
}

.report-section {
  flex: 1 1 0;
  min-height: 0;
  border-radius: 8px;
  padding: 15px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.series-table-section {
  flex: 0 1 auto;
  min-height: 0;
  max-height: 40%;
  border: none;
  padding: 0;
  overflow: hidden;
  background-color: transparent;
}

.series-table-wrapper {
  flex: 0 1 auto; /* was 1 1 0 — let each table be its own content height */
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.controls-row {
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.orientation-selector {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  border-radius: 6px;
  padding: 10px;
}

.checkbox-row {
  display: flex;
  align-items: center;
  gap: 10px;
  align-self: flex-end;
}

.tool-active {
  background: rgba(var(--v-theme-primary), 0.15);
  border-bottom: 2px solid var(--md-primary);
  color: var(--md-primary);
}

.orientation-selector select {
  padding: 0px 10px;
  border-radius: 4px;
  border: 1px solid var(--md-surface-container-highest);
  background-color: var(--md-surface-container-high);
  width: 150px;
  font-size: 0.75rem;
  align-self: flex-end;
}

.function-tables-container {
  display: flex;
  gap: 20px;
  width: 100%;
  min-height: 0;
}

.tables-section {
  flex: 2;
  display: flex;
  flex-direction: row;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: space-between;
  min-height: 0;
}
</style>
